import { useEffect, useState } from 'react'
import { gql } from 'urql'
import { v4 as uuidv4 } from 'uuid'
import Uppy from '@uppy/core'
import { useUppy } from '@uppy/react'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import { ConfirmModal, Fetching } from '@innovatrics/checkin-components'
import { useNavigate, useParams } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'

import {
  useCheckininsertMutation,
  useUpdateCheckinCustomerMutation,
} from '../generated/graphql'
import { Footer } from '../main-layout/Footer'
import { CheckinIntroduction, Results } from '.'
import {
  AutoCapture,
  DocumentReviewContainer,
  ScanFrontPageConfirm,
} from '../components'
import { useLastCustomer } from '../components/LastCustomerContext'

gql`
  subscription GetLastCustomer($customerId: uuid!) {
    checkinCustomer(id: $customerId) {
      ...GetLastCustomerFields
    }
  }

  fragment GetLastCustomerFields on checkinCustomer {
    id
    surname
    givenName
    dateOfBirth
    dateOfExpiry
    dateOfArrival
    address
    nationality
    documentNumber
    documentType
    email
    checkinNumber
    uploadedFrontPagePath
    uploadedBackPagePath
    frontPageType
    backPageType
    isConfirmed
    isOcrDone
    isScanDone
  }

  mutation Checkininsert($object: checkin_insert_input!) {
    insertCheckin(object: $object) {
      ...InsertCheckinFields
    }
  }

  mutation UpdateCheckinCustomer(
    $id: uuid!
    $object: checkinCustomer_set_input!
  ) {
    updateCheckinCustomer(pk_columns: { id: $id }, _set: $object) {
      id
    }
  }

  fragment InsertCheckinFields on checkin {
    checkinCustomers {
      id
      checkinId
    }
  }
`

export enum Steps {
  INTRODUCTION,
  SCAN_DOCUMENT_FRONT_PAGE,
  SCAN_DOCUMENT_FRONT_PAGE_CONFIRM,
  SCAN_DOCUMENT_BACK_PAGE,
  DOCUMENT_REVIEW,
  RESULTS,
}

interface AutoCaptureContainerProps {
  hotelName: string
  organizationId: string
}

export const AutoCaptureContainer = ({
  hotelName,
  organizationId,
}: AutoCaptureContainerProps) => {
  const { hash, id } = useParams()
  const navigate = useNavigate()
  const matches = useMediaQuery('(min-width:750px)')

  const [{ fetching: fetchingCheckin, data: checkinData }, insertCheckin] =
    useCheckininsertMutation()

  const [{ fetching: fetchingCustomer, data: customerData }, updateCustomer] =
    useUpdateCheckinCustomerMutation()

  const customerId =
    checkinData?.insertCheckin?.checkinCustomers?.[0]?.id ?? id ?? undefined

  const [res, fetchingSubscription] = useLastCustomer()

  console.log('subs', res, fetchingSubscription)

  const checkinCustomer = res?.data
  const checkinWasDeleted = checkinCustomer?.isDeleted

  const [errorModalVisible, setErrorModalVisible] = useState(false)
  const [buttonState, setButtonState] = useState<boolean>(false)
  const [staticResult, setStaticResult] = useState()
  const [activeStep, setActiveStep] = useState<Steps>(Steps.INTRODUCTION)

  useEffect(() => {
    setActiveStep(
      staticResult
        ? Steps.RESULTS
        : checkinCustomer?.isConfirmed
        ? Steps.RESULTS
        : checkinCustomer?.isScanDone
        ? Steps.DOCUMENT_REVIEW
        : checkinCustomer?.uploadedFrontPagePath
        ? Steps.SCAN_DOCUMENT_FRONT_PAGE_CONFIRM
        : checkinCustomer
        ? Steps.SCAN_DOCUMENT_FRONT_PAGE
        : Steps.INTRODUCTION
    )
  }, [checkinCustomer])

  const uppy = useUppy(() =>
    new Uppy().use(AwsS3Multipart, {
      companionUrl: 'https://api.magicsk.eu',
    })
  )

  const handlePhotoTaken = (image: Blob) => {
    try {
      uppy.addFile({
        name: `${uuidv4()}.jpeg`,
        type: image.type,
        data: image,
      })
      uppy
        .upload()
        .then((result) => {
          const s3uri = result.successful[0]?.uploadURL
          if (activeStep === Steps.SCAN_DOCUMENT_FRONT_PAGE) {
            setActiveStep(Steps.SCAN_DOCUMENT_FRONT_PAGE_CONFIRM)
            updateCustomer({
              object: { uploadedFrontPagePath: s3uri },
              id: customerId,
            }).then((res) => {
              if (res.error) {
                setErrorModalVisible(true)
                throw new Error(res.error.message ?? 'Upload error')
              }
            })
          } else {
            setActiveStep(Steps.DOCUMENT_REVIEW)
            updateCustomer({
              object: {
                uploadedBackPagePath: s3uri,
                isScanDone: true,
              },
              id: customerId,
            }).then((res) => {
              if (res.error) {
                setErrorModalVisible(true)
                throw new Error(res.error.message ?? 'Upload error')
              }
            })
          }
          if (result.failed.length > 0) {
            console.error('Errors:')
            result.failed.forEach((file) => {
              console.error(file.error)
            })
            setErrorModalVisible(true)
          }
        })
        .catch((error) => {
          throw error
        })
    } catch (error) {
      console.log('Error' + error)
      setErrorModalVisible(true)
    }
  }

  const getCheckinNumber = () =>
    Math.floor(Math.random() * (999999 - 100000) + 100000)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '-')

  const initializeCheckin = () => {
    setButtonState(true)
    try {
      insertCheckin({
        object: {
          checkinCustomers: { data: [{ checkinNumber: getCheckinNumber() }] },
          organizationId,
        },
      }).then(({ data, error }) => {
        setButtonState(false)
        if (error?.message.includes('duplicate key')) {
          initializeCheckin()
        } else if (error) {
          setErrorModalVisible(true)
          throw new Error(error.message ?? 'Unable to create a checkin!')
        } else {
          navigate(`/${hash}/${data?.insertCheckin?.checkinCustomers?.[0]?.id}`)
        }
      })
    } catch (error) {
      setErrorModalVisible(true)
      console.log(error)
    }
  }

  const skipBackPage = () => {
    setActiveStep(Steps.DOCUMENT_REVIEW)
    updateCustomer({
      object: { isScanDone: true },
      id: customerId,
    })
  }

  const getCurrentStep = (step: Steps) => {
    switch (step) {
      case Steps.INTRODUCTION:
        return (
          <CheckinIntroduction
            buttonState={buttonState}
            initializeCheckin={initializeCheckin}
            hotelName={hotelName}
          />
        )
      case Steps.SCAN_DOCUMENT_FRONT_PAGE:
      case Steps.SCAN_DOCUMENT_BACK_PAGE:
        return <AutoCapture handlePhotoTaken={handlePhotoTaken} />
      case Steps.SCAN_DOCUMENT_FRONT_PAGE_CONFIRM:
        return (
          <ScanFrontPageConfirm
            setStep={setActiveStep}
            skipBackPage={skipBackPage}
          />
        )
      case Steps.DOCUMENT_REVIEW:
        return checkinCustomer?.isOcrDone ? (
          <DocumentReviewContainer
            data={checkinCustomer}
            setStep={setActiveStep}
            setStaticResult={setStaticResult}
          />
        ) : (
          <Fetching queryError={res.error} />
        )

      case Steps.RESULTS:
        return <Results hotelName={hotelName} data={staticResult} />
    }
  }

  const isExpired = checkinCustomer?.isExpired
  if (
    id &&
    !checkinCustomer &&
    !fetchingCustomer &&
    !staticResult &&
    !fetchingSubscription
  ) {
    navigate(`/reset/${hash}`)
  }
  if (checkinWasDeleted || isExpired) navigate('/deleted')

  if (
    (id && !checkinCustomer && !staticResult) ||
    fetchingCustomer ||
    fetchingCheckin
  )
    return <Fetching />

  return (
    <>
      <div
        style={{
          minHeight: matches ? 'calc(100vh - 192px)' : 'calc(100vh - 226px)',
        }}
      >
        {getCurrentStep(activeStep)}
        <ConfirmModal
          name={'customer.error'}
          modalOpen={errorModalVisible}
          setModalOpen={setErrorModalVisible}
          handleClick={() => setErrorModalVisible(false)}
        />
      </div>
      <Footer
        white={activeStep == Steps.INTRODUCTION || activeStep == Steps.RESULTS}
      />
    </>
  )
}
